/**
 * AdminIntegrationKeysCard — manage 3rd-party API keys from the UI.
 * DB values override `.env`. Secret keys show masked previews until the
 * admin starts typing a new value; clearing the field reverts to env.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Key, Eye, EyeSlash, FloppyDisk, ArrowsClockwise,
  Spinner, CheckCircle, XCircle, Plug,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SourcePill = ({ source }) => {
  const map = {
    db:      ['DB',      'bg-emerald-100 text-emerald-700 border-emerald-300'],
    env:     ['ENV',     'bg-blue-100 text-blue-700 border-blue-300'],
    missing: ['UNSET',   'bg-gray-100 text-gray-600 border-gray-300'],
  };
  const [label, cls] = map[source] || map.missing;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${cls}`}
      data-testid={`integration-key-source-${source}`}>
      {label}
    </span>
  );
};

async function fetchKeys() {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/admin/integration-keys`,
    { withCredentials: true },
  );
  return data?.keys || [];
}

async function saveKeys(values) {
  const { data } = await axios.put(
    `${BACKEND_URL}/api/admin/integration-keys`,
    { values },
    { withCredentials: true },
  );
  return data;
}

async function testProvider(provider) {
  const { data } = await axios.post(
    `${BACKEND_URL}/api/admin/integration-keys/test`,
    { provider },
    { withCredentials: true },
  );
  return data;
}

const KeyRow = ({ meta, draftValue, onChange, reveal, toggleReveal }) => {
  const isDraft = draftValue !== undefined;
  const showRevealedDraft = isDraft && reveal;
  const valueToShow = isDraft
    ? draftValue
    : (meta.secret ? meta.value_preview : (meta.value_preview || ''));

  return (
    <div className="border border-[#E8E1D5] bg-white rounded p-3"
      data-testid={`integration-key-row-${meta.key}`}>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{meta.label}</span>
          <code className="text-[10px] text-text-secondary font-mono">{meta.key}</code>
          <SourcePill source={meta.source} />
          {meta.secret && (
            <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded uppercase font-bold">
              secret
            </span>
          )}
        </div>
        {meta.secret && (
          <button
            type="button"
            onClick={toggleReveal}
            className="text-text-secondary hover:text-text-primary text-xs flex items-center gap-1"
            data-testid={`integration-key-reveal-${meta.key}`}
          >
            {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
            {reveal ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      <Input
        type={(meta.secret && !showRevealedDraft && isDraft) ? 'password' : 'text'}
        value={valueToShow}
        onChange={(e) => onChange(e.target.value)}
        placeholder={meta.secret
          ? (meta.has_value ? 'Paste new value to override' : 'Enter API key')
          : 'Enter value'}
        className="font-mono text-xs"
        data-testid={`integration-key-input-${meta.key}`}
      />
      {meta.hint && (
        <p className="text-[11px] text-text-secondary mt-1">{meta.hint}</p>
      )}
      {isDraft && (
        <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
          Unsaved change — {draftValue === ''
            ? 'will clear DB override (env value resurfaces)'
            : 'will save as DB override'}
        </p>
      )}
    </div>
  );
};

const AdminIntegrationKeysCard = () => {
  const [keys, setKeys] = useState([]);
  const [draft, setDraft] = useState({});   // {KEY: newValue}
  const [reveal, setReveal] = useState({}); // {KEY: bool}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);   // provider id
  const [testResults, setTestResults] = useState({}); // {provider: {ok, http, response}}

  useEffect(() => {
    let mounted = true;
    fetchKeys()
      .then((rows) => { if (mounted) { setKeys(rows); setLoading(false); } })
      .catch((err) => {
        if (mounted) {
          toast.error(err?.response?.data?.detail || 'Failed to load integration keys');
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, []);

  const onFieldChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const discard = (key) => {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onSave = () => {
    if (Object.keys(draft).length === 0) {
      toast.info('Nothing to save');
      return;
    }
    setSaving(true);
    saveKeys(draft)
      .then((res) => {
        setKeys(res.keys || []);
        setDraft({});
        const counts = Object.values(res.applied || {}).reduce((acc, v) => {
          acc[v] = (acc[v] || 0) + 1;
          return acc;
        }, {});
        const parts = [];
        if (counts.set) parts.push(`${counts.set} saved`);
        if (counts.cleared) parts.push(`${counts.cleared} cleared`);
        toast.success(parts.join(' · ') || 'Saved');
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const onRefresh = () => {
    setLoading(true);
    fetchKeys()
      .then((rows) => { setKeys(rows); setDraft({}); setLoading(false); })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || 'Refresh failed');
        setLoading(false);
      });
  };

  const onTest = (provider) => {
    setTesting(provider);
    testProvider(provider)
      .then((res) => {
        setTestResults((prev) => ({ ...prev, [provider]: res }));
        toast[res?.ok ? 'success' : 'error'](
          res?.ok
            ? `${provider} reachable ✓`
            : `${provider} test failed (HTTP ${res?.http || '?'})`,
        );
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Test failed'))
      .finally(() => setTesting(null));
  };

  const groups = keys.reduce((acc, k) => {
    acc[k.group] = acc[k.group] || [];
    acc[k.group].push(k);
    return acc;
  }, {});

  const groupTestMap = {
    '2factor.in OTP': { id: 'twofactor', label: 'Test 2factor.in' },
    'MSG91 OTP SMS':  { id: 'msg91_otp', label: 'Test MSG91' },
  };

  const draftCount = Object.keys(draft).length;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-integration-keys-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Key size={20} weight="duotone" />
              Integration Keys
            </CardTitle>
            <CardDescription>
              Manage 3rd-party API keys without touching <code>.env</code>.
              <strong> Saved values override env</strong> — clear a field to
              fall back to the env value.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh}
              disabled={loading}
              data-testid="integration-keys-refresh-btn">
              <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button onClick={onSave}
              disabled={saving || draftCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="integration-keys-save-btn">
              {saving
                ? <Spinner size={14} className="animate-spin mr-2" />
                : <FloppyDisk size={14} className="mr-2" weight="fill" />}
              Save{draftCount > 0 ? ` (${draftCount})` : ''}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6 text-sm text-text-secondary">Loading…</div>
        ) : (
          Object.entries(groups).map(([group, rows]) => {
            const test = groupTestMap[group];
            const result = test && testResults[test.id];
            return (
              <div key={group} className="mb-5"
                data-testid={`integration-keys-group-${group.replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h3 className="font-bold text-sm uppercase tracking-wide text-text-secondary">
                    {group}
                  </h3>
                  {test && (
                    <div className="flex items-center gap-2">
                      {result && (
                        <span className={`flex items-center gap-1 text-[11px] ${
                          result.ok ? 'text-emerald-700' : 'text-red-600'
                        }`}
                          data-testid={`integration-keys-test-result-${test.id}`}>
                          {result.ok
                            ? <CheckCircle size={14} weight="fill" />
                            : <XCircle size={14} weight="fill" />}
                          HTTP {result.http}
                        </span>
                      )}
                      <Button variant="outline" size="sm"
                        onClick={() => onTest(test.id)}
                        disabled={testing === test.id}
                        data-testid={`integration-keys-test-btn-${test.id}`}>
                        {testing === test.id
                          ? <Spinner size={12} className="animate-spin mr-1" />
                          : <Plug size={12} className="mr-1" weight="fill" />}
                        {test.label}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {rows.map((meta) => (
                    <div key={meta.key}>
                      <KeyRow
                        meta={meta}
                        draftValue={draft[meta.key]}
                        onChange={(v) => onFieldChange(meta.key, v)}
                        reveal={reveal[meta.key]}
                        toggleReveal={() =>
                          setReveal((prev) => ({ ...prev, [meta.key]: !prev[meta.key] }))
                        }
                      />
                      {draft[meta.key] !== undefined && (
                        <button
                          type="button"
                          onClick={() => discard(meta.key)}
                          className="text-[11px] text-text-secondary underline mt-1 ml-1"
                          data-testid={`integration-key-discard-${meta.key}`}
                        >
                          discard change
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
        <p className="text-[11px] text-text-secondary border-t border-[#E8E1D5] pt-3 mt-3">
          <strong>How it resolves:</strong> Backend reads each key from
          MongoDB <code>admin_settings.integration_keys</code> first; if
          empty, falls back to <code>.env</code>. Changes propagate within 30
          seconds (in-process cache TTL).
        </p>
      </CardContent>
    </Card>
  );
};

export default AdminIntegrationKeysCard;
